import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { wishlistAPI } from '../utils/api';
import { toast } from 'sonner';

/**
 * useWishlistToggle — shared heart-button state for any listing card grid.
 *
 * Fetches the current user's wishlist once on mount (per user change), keeps
 * the set of saved listing_ids in state, and returns:
 *   - `isSaved(listing_id)` → boolean for filled/outlined heart
 *   - `toggle(event, listing_id)` → optimistic add/remove with toast + rollback
 *
 * Guests are redirected to /login on toggle. Errors roll back the optimistic
 * update and surface a toast — the caller doesn't need any try/catch.
 *
 * Used by: Homepage, SearchResults, CategoryBrowse (iter118).
 */
export default function useWishlistToggle(user) {
  const [ids, setIds] = useState(() => new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await wishlistAPI.getAll();
        const s = new Set((res.data || []).map((i) => i.listing_id));
        if (!cancelled) setIds(s);
      } catch (_) { /* silent — heart just stays unfilled */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const isSaved = useCallback((listingId) => ids.has(listingId), [ids]);

  const toggle = useCallback(async (e, listingId) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    const wasSaved = ids.has(listingId);
    setIds((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(listingId); else next.add(listingId);
      return next;
    });
    try {
      if (wasSaved) await wishlistAPI.remove(listingId);
      else await wishlistAPI.add(listingId);
      toast.success(wasSaved ? 'Removed from wishlist' : 'Added to wishlist');
    } catch (err) {
      setIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(listingId); else next.delete(listingId);
        return next;
      });
      toast.error('Failed to update wishlist');
    }
  }, [ids, user, navigate]);

  return { isSaved, toggle };
}
